import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { LIST_IDENTIFIERS_RESOURCE } from 'src/features/membership/api/identifier/useListIdentifiers';
import { useArchiveIdentifier } from 'src/features/membership/api/identifier/useArchiveIdentifier';
import { MembershipIdentifier } from 'src/features/membership/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { ICON_MAP } from 'src/utils/constants';

interface IdentifierFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedIdentifier: MembershipIdentifier;
    children?: React.ReactNode;
    membershipId?: string;
}

function ArchiveIdentifierFormModal<TData>({
    selectedIdentifier,
    children,
    membershipId = '',
}: IdentifierFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();

    const { mutate: archiveIdentifier, isPending: isPendingArchive } = useArchiveIdentifier({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_IDENTIFIERS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(identifiersMessages.archiveIdentifierSuccess),
                description: formatMessage(identifiersMessages.archiveIdentifierSuccessDescription, {
                    entry: selectedIdentifier?.identifier.value,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => archiveIdentifier({ parkingId, identifierId: selectedIdentifier.id, membershipId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={ICON_MAP.ARCHIVE} />
                    <div>
                        <DialogTitle>
                            <FormattedMessage {...identifiersMessages.archiveIdentifierTitle} />
                        </DialogTitle>
                        <DialogDescription>
                            <FormattedMessage
                                {...identifiersMessages.archiveIdentifierSubtitle}
                                values={{ entry: selectedIdentifier.identifier.value }}
                            />
                        </DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            {...identifiersMessages.confirmArchiveIdentifier}
                            values={{ entry: selectedIdentifier.identifier.value }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPendingArchive}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingArchive && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage {...identifiersMessages.archiveIdentifierAction} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ArchiveIdentifierFormModal;
